import React, { useState } from "react";
import { Navigate, useLocation } from "react-router";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import { Loader2, ShieldCheck } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import ColorSchemeToggle from "../components/common/ColorSchemeToggle";
import { useAuth } from "../contexts/AuthContext";
import useApi from "../services/ApiService";

interface McpAuthorizationRequestInfo {
  clientName: string;
  resourceName: string;
  resourceUrl: string;
  userName: string;
  denyRedirectUrl: string;
  redirectsToThisComputer: boolean;
  redirectHost: string;
}

interface McpOAuthError {
  error: string;
  error_description: string;
}

/**
 * The consent step of MCP sign-in: an MCP client (Claude Code, Claude desktop, claude.ai) sends the user here with
 * its OAuth authorize request, the signed-in portal user allows or denies it, and the browser goes back to the
 * client. Viewers are refused by the API and told so.
 */
const McpAuthorizePage: React.FC = () => {
  const { isAuthenticated } = useAuth();
  const location = useLocation();

  if (!isAuthenticated) {
    const returnTo = `${location.pathname}${location.search}`;
    return <Navigate to={`/login?returnTo=${encodeURIComponent(returnTo)}`} replace />;
  }

  return <McpAuthorizeConsent search={location.search} />;
};

const McpAuthorizeConsent: React.FC<{ search: string }> = ({ search }) => {
  const api = useApi();
  const [approving, setApproving] = useState(false);
  const [approvalError, setApprovalError] = useState<string | null>(null);

  const request = useQuery({
    queryKey: ["mcp-authorization-request", search],
    queryFn: async () =>
      (await api.get<McpAuthorizationRequestInfo>(`api/oauth/authorization-request${search}`)).data,
    retry: false,
  });

  const allow = async () => {
    setApproving(true);
    setApprovalError(null);
    const parameters = new URLSearchParams(search);
    try {
      const response = await api.post<{ redirectUrl: string }>("api/oauth/authorize", {
        responseType: parameters.get("response_type"),
        clientId: parameters.get("client_id"),
        redirectUri: parameters.get("redirect_uri"),
        codeChallenge: parameters.get("code_challenge"),
        codeChallengeMethod: parameters.get("code_challenge_method"),
        resource: parameters.get("resource"),
        state: parameters.get("state"),
      });
      window.location.assign(response.data.redirectUrl);
    } catch (error) {
      setApprovalError(describeError(error));
      setApproving(false);
    }
  };

  return (
    <div className="relative flex flex-1 items-center justify-center bg-gradient-to-br from-background via-background to-primary/5 dark:to-primary/10 p-4">
      <div className="absolute top-4 right-4">
        <ColorSchemeToggle />
      </div>
      <div className="w-full max-w-md animate-in fade-in slide-in-from-bottom-4 duration-500">
        <div className="flex flex-col items-center mb-6">
          <h1 className="text-xl font-bold tracking-tight">Neos Files Sync</h1>
          <p className="text-sm text-muted-foreground mt-1">Connect an AI assistant</p>
        </div>
        <Card className="w-full">
          <CardContent className="pt-6 space-y-4">
            {request.isPending && (
              <div className="flex justify-center py-6">
                <Loader2 className="size-5 animate-spin text-muted-foreground" />
              </div>
            )}

            {request.isError && (
              <Alert variant="destructive">
                <AlertDescription>{describeError(request.error)}</AlertDescription>
              </Alert>
            )}

            {request.data && (
              <>
                <div className="flex items-start gap-3">
                  <ShieldCheck className="size-5 mt-0.5 shrink-0 text-primary" />
                  <p className="text-sm">
                    <span className="font-medium">{request.data.clientName}</span> wants to use{" "}
                    <span className="font-medium">{request.data.resourceName}</span> as{" "}
                    <span className="font-medium">{request.data.userName}</span>.
                  </p>
                </div>
                <p className="text-sm text-muted-foreground">
                  It gets the same access you have in the portal, for as long as you stay connected.
                </p>
                {/* Anyone can register an app under any name, so the name alone cannot tell the user who gets in. */}
                {request.data.redirectsToThisComputer ? (
                  <p className="text-sm text-muted-foreground">Allowing it signs in an app on this computer.</p>
                ) : (
                  <Alert>
                    <AlertDescription>
                      Allowing it sends the sign-in to{" "}
                      <span className="font-medium text-foreground">{request.data.redirectHost}</span>. Any app can
                      call itself anything, so allow it only if you started this connection yourself.
                    </AlertDescription>
                  </Alert>
                )}
                {approvalError && (
                  <Alert variant="destructive">
                    <AlertDescription>{approvalError}</AlertDescription>
                  </Alert>
                )}
                <div className="flex gap-2 justify-end">
                  <Button
                    variant="outline"
                    disabled={approving}
                    onClick={() => window.location.assign(request.data.denyRedirectUrl)}
                  >
                    Deny
                  </Button>
                  <Button disabled={approving} onClick={allow}>
                    {approving && <Loader2 className="size-4 animate-spin" />}
                    Allow
                  </Button>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

const describeError = (error: unknown): string => {
  if (axios.isAxiosError(error)) {
    if (error.response?.status === 403)
      return "Your role does not include access to the AI assistant connection. Ask an Admin if you need it.";
    const oauthError = error.response?.data as McpOAuthError | undefined;
    if (oauthError?.error === "invalid_client")
      return "NFS no longer knows this connection. Remove NFS from your AI assistant, add it again and sign in.";
    if (oauthError?.error_description) return oauthError.error_description;
  }
  return "The sign-in request could not be checked. Start the connection again from your AI assistant.";
};

export default McpAuthorizePage;
